/* eslint-disable max-lines */
/* eslint-disable complexity */
import { Blueprint, ComponentControllerBase, ControllerRootOptions, DesignContext, EngineConfigOverrides, IControllerBlueprint, Kitchen, Publish, Vertex3 } from '@rta/rta-blueprint-library';
import {
	computeAutoResizeWidthCfn,
	getGroupAnchorItemCfn,
	getRequiredPanelWidthCfn,
	isRuleGeneratedCompanionCfn,
	moveApplianceGroupToPanelCfn,
	planGroupLayoutCfn,
	swapApplianceGroupPositionsAcrossPanelsCfn
} from '@rta/rta-blueprint-library';
import type { Island, Panel } from '@rta/rta-blueprint-library';
import type {
	ApplianceDragOptions,
	ApplianceDragProvider,
	ApplianceDragState,
	ApplianceDropAssessment,
	ApplianceDropRequest,
	ApplianceDropResult,
	DragIssue,
	DragIssueSeverity,
	DragIslandModel,
	DragModel,
	GrowthPrediction,
	PlanLayoutInput
} from '../core/engine/managers/appliance-drag-manager/types';
import { DesignerOptions } from './designer-options.class';
import { registerSceneObjectType, type SceneCategory } from '../core/interaction';
import { executeTool } from './functions/execute-tool.cfn';
import { EXPLORE_MARKER_CATEGORIES, EXPLORE_MARKER_TYPE, ExploreMarkerCategory, hideExploreMarkersCfn, showExploreMarkersCfn } from './functions/explore-markers';
import { hideValidationMarkersCfn, showValidationMarkersCfn } from './functions/validation-markers';
import { getToolDefinitions } from './functions/get-tool-definitions.cfn';
import { ToolContext } from './functions/tool-context.type';
import { ControllerDataIn } from './types/controller-data.type';
import { DesignerOptionsData } from './types/designer-options-data.type';
import { IRender3dKitchenController, RendererInfoSnapshot, UseThreeKitchenReturn } from './types/render3d-kitchen-controller.interface';
import { SceneChangeHints, ToolDefinition, ToolResult } from './types/tool.types';
import { Viewport } from './Viewport.class';

const ISSUE_SEVERITY: Record<number, DragIssueSeverity> = { 1: 'info', 2: 'warn', 4: 'error', 8: 'critical' };

/** The rules engine answers the same kitchen event, so the badges wait for the notifications it rewrites. */
const VALIDATION_MARKER_REFRESH_MS = 200;

/**
 * The kitchen's validation notifications as drag issues. The library words each one as
 * "Location: <where> · Issue: <what>"; the two parts split here so the floor label can show
 * the problem alone. The key tells the same notification apart across a copy of the kitchen.
 */
const readKitchenIssuesCfn = (kitchen: Kitchen): Array<DragIssue & { key: string }> =>
	kitchen.design.notifications.children.map((notification) => {
		const description = String(notification.description ?? '');
		const match = /^Location:\s*([\s\S]*?)\s*·\s*Issue:\s*([\s\S]*)$/.exec(description);
		return {
			severity: ISSUE_SEVERITY[Number(notification.type)] ?? 'warn',
			message: match ? match[2].trim() : description,
			location: match ? match[1].trim() : null,
			code: String(notification.validationCode ?? ''),
			isNew: false,
			key: `${String(notification.validationCode ?? '')}|${String(notification.refId ?? '')}|${description}`
		};
	});

export class Kitchen3dController extends ComponentControllerBase<ControllerDataIn> implements IControllerBlueprint, IRender3dKitchenController {
	#blueprint: Blueprint | null;
	#forceRenderCount: number;
	#isRendering: boolean;
	#isZooming: boolean;
	#kitchen: Kitchen | null;
	#kitchenVersion: number | null;
	#zoom: number;
	#image: string | null;
	#floorKey: string;
	#skyKey: string;
	#customTexture: string | null;
	#viewport: Viewport;
	#designerOptions: DesignerOptions;
	#threeInstanceRef: UseThreeKitchenReturn | null;
	#showTools: boolean;
	#isVisible: boolean;
	#focalOffset: { x: number; y: number };
	#viewportAnchor: { vertical?: number; horizontal?: number };
	#progressiveRender: boolean;
	#preloadAssets: boolean;
	#engineConfig: EngineConfigOverrides | null;
	#pendingChanges: SceneChangeHints = {};
	#noModelSkus: string[] = ['PASSTHROUGH', 'VENT-SIDE', 'VENT-BACK', 'MOK-VENT-SIDE', 'MOK-VENT-BACK'];
	#microtaskScheduled = false;
	#applyChain: Promise<void> = Promise.resolve();
	#eventSubscriptionIds: Array<string> = [];
	#pendingGizmoReactivation: string | null = null;
	#dragEnabled = false;
	#dragOptions: ApplianceDragOptions = {};
	#dragProvider: ApplianceDragProvider = this.#createDragProvider();
	/** True while a drag commit mutates the model: domain events are not turned into scene hints, one explicit rebuild follows */
	#hintsSuspended = false;
	#exploreHoverEnabled = false;
	#exploreMarkersEnabled = false;
	#exploreMarkerCategories: Array<ExploreMarkerCategory> = EXPLORE_MARKER_CATEGORIES;
	#validationMarkersEnabled = false;
	#validationMarkerRefresh: ReturnType<typeof setTimeout> | null = null;

	#logPendingChanges (label: string): void {
		const pc = this.#pendingChanges;
		console.log(`[K3dCtrl] ${label}`, {
			removed: pc.removed?.length ?? 0,
			islandsRemoved: pc.islandsRemoved?.length ?? 0,
			islandsAdded: pc.islandsAdded?.length ?? 0,
			added: pc.added?.length ?? 0,
			moved: pc.moved?.length ?? 0,
			materials: pc.materials?.length ?? 0,
			groupsMoved: pc.groupsMoved?.length ?? 0,
			islandResized: pc.islandResized?.length ?? 0,
			islandRotated: pc.islandRotated?.length ?? 0,
			islandPositionsUpdated: pc.islandPositionsUpdated ?? false,
			panelDeleted: pc.panelDeleted?.length ?? 0,
			surroundMaterialChanged: pc.surroundMaterialChanged?.key ?? null,
			countertopMaterialChanged: pc.countertopMaterialChanged?.key ?? null
		});
	}

	readonly options: DesignerOptionsData;

	// Progressive rendering callbacks
	onObjectAdded?: (objectId: string, objectType: string) => void;
	onObjectRemoving?: (objectId: string) => void;

	constructor (blueprint?: Blueprint | null, options: ControllerRootOptions<ControllerDataIn> = {}) {
		super('td-controller', blueprint ?? undefined, { eventGroup: 'td-component', ...options });

		this.#blueprint = blueprint ?? null;
		this.#forceRenderCount = 0;
		this.#zoom = 100;
		this.#isRendering = false;
		this.#isZooming = false;
		this.#viewport = new Viewport({ zoom: 1 });

		this.#floorKey = 'floor/grid-tile'; // fallback default — used only when #kitchen is null
		this.#skyKey = 'sky/overcast'; // fallback default — used only when #kitchen is null
		this.#customTexture = null;

		this.#kitchen = blueprint?.kitchens?.getActiveVersion() ?? null;
		this.#kitchenVersion = this.#kitchen?.version ?? null;

		this.#image = null;

		this.#designerOptions = new DesignerOptions({ dataIn: options.dataIn?.options ?? {}, parent: this }, this);
		this.options = this.#designerOptions as unknown as DesignerOptionsData;
		this.#threeInstanceRef = null;
		this.#showTools = (options as any).showTools ?? true;
		this.#isVisible = true;
		this.#focalOffset = { x: 0, y: 0 };
		this.#viewportAnchor = { vertical: 0.5, horizontal: 0.5 };
		this.#progressiveRender = (options as any).progressiveRender ?? false;
		this.#preloadAssets = (options as any).preloadAssets ?? true;
		this.#engineConfig = options.engineConfig ?? null;

		this.#subscribeToBlueprintEvents();
		this.setInitialized();
	}

	get blueprint (): Blueprint | null { return this.#blueprint; }
	get forceRenderCount (): number { return this.#forceRenderCount; }
	get kitchen (): Kitchen | null { return this.#kitchen; }
	get isZooming (): boolean { return this.#isZooming; }
	get isRendering (): boolean { return this.#isRendering; }

	get viewport (): Viewport { return this.#viewport; }
	get readonly (): boolean {
		return (this.kitchen?.version ?? null) === null ? this.options.readonly : true;
	}

	/** Renderer overrides supplied at construction (or null if none). */
	get engineConfig (): EngineConfigOverrides | null { return this.#engineConfig; }

	/** Live snapshot of the WebGLRenderer (for telemetry / leak detection). */
	get rendererInfo (): RendererInfoSnapshot | null {
		return this.#threeInstanceRef?.getRendererInfo?.() ?? null;
	}

	@Publish('kitchen_version', 'updated')
	set kitchenVersion (version: number | null) {
		let kv = this.#blueprint.kitchens.findVersion(version);
		if (!kv) {
			if (version === null) kv = this.#blueprint.kitchens.newVersionFromLatest();
			else kv = this.#blueprint.kitchens.getActiveVersion();
		}
		this.#kitchen = kv;
		this.#kitchenVersion = version;
	}
	get kitchenVersion (): number | null { return this.#kitchenVersion; }

	@Publish('zoom', 'updated')
	set zoom (zoom: number) {
		this.#zoom = zoom;
	}
	get zoom (): number {
		if (this.#zoom < 40) this.#zoom = 40;
		if (this.#zoom > 1500) this.#zoom = 1500;
		return this.#zoom;
	}
	@Publish('floor_key', 'updated')
	set floorKey (floorKey: string) {
		this.#floorKey = floorKey;
		if (this.#kitchen) this.#kitchen.floorKey = floorKey;
	}
	get floorKey (): string {
		return this.#kitchen?.floorKey ?? this.#floorKey;
	}
	@Publish('sky_key', 'updated')
	set skyKey (skyKey: string) {
		this.#skyKey = skyKey;
		if (this.#kitchen) this.#kitchen.skyKey = skyKey;
	}
	get skyKey (): string {
		return this.#kitchen?.skyKey ?? this.#skyKey;
	}

	@Publish('custom_texture', 'updated')
	set customTexture (customTexture: string | null) {
		this.#customTexture = customTexture;
	}
	get customTexture (): string | null {
		return this.#customTexture;
	}

	get textureQuality (): number | null {
		return (this.options as { textureQuality?: number | null }).textureQuality ?? null;
	}

	/**
	 * Apply a new sky texture quality tier. Updates `options.textureQuality`,
	 * forwards to the 3D engine to upgrade/clear the sky, and publishes a
	 * `texture_quality.updated` event. Returns a promise that resolves once the
	 * sky has finished loading at the new tier so callers can drive a spinner.
	 */
	@Publish('texture_quality', 'updated')
	public async setTextureQuality (quality: number | null): Promise<void> {
		(this.options as { textureQuality: number | null }).textureQuality = quality;
		const apply = (this.#threeInstanceRef as { setTextureQuality?: (q: number | null) => Promise<void> } | null)
			?.setTextureQuality;
		if (apply) {
			await apply.call(this.#threeInstanceRef, quality);
		}
	}

	public setCustomTexture (textureData: string): void {
		this.customTexture = textureData;
	}

	@Publish('image', 'snapshot')
	public getImage (): string {
		return this.#image;
	}

	public setImage (imageData: string): void {
		if (imageData !== '' && imageData !== null) {
			this.#image = imageData;
		}
	}

	/** Live on-screen WebGL canvas — the AI-rendering live preview blits from this each frame. */
	public getCanvasElement (): HTMLCanvasElement | null {
		return this.#threeInstanceRef?.getCanvasElement?.() ?? null;
	}

	/**
	 * Renders a capture-aspect image directly from the engine that owns this controller's instance
	 * slot, bypassing `executeTool('take_screenshot')` so it never calls `setImage` — a preview
	 * framing a shot must not overwrite the shared thumbnail cache other features read. Resolves
	 * `null` when no engine owns the slot (nothing to capture from).
	 */
	public getLiveImageRender (pixelRatio = 2): Promise<string | null> {
		return new Promise((resolve) => {
			if (!this.#threeInstanceRef?.getImageRender) {
				resolve(null);
				return;
			}
			this.#threeInstanceRef.getImageRender(pixelRatio, (imageData: string) => resolve(imageData || null));
		});
	}

	/**
	 * Export the current scene as a GLB ArrayBuffer without triggering a browser download.
	 * Used by the headless capture flow for presentation generation.
	 */
	async exportGLB (options: { maxTextureSize?: number; includeFloor?: boolean; includeSky?: boolean; readinessTimeoutMs?: number } = {}): Promise<ArrayBuffer | null> {
		if (!this.#threeInstanceRef) throw new Error('3D instance not mounted — cannot export GLB');
		return this.#threeInstanceRef.exportModel?.({ skipDownload: true, includeFloor: true, includeSky: true, ...options }) ?? null;
	}

	/**
	 * Wait until the scene is ready (all manifested appliances and materials applied).
	 * Rejects with SceneNotReadyError if not ready within timeoutMs.
	 */
	async waitForSceneReady (options?: { timeoutMs?: number }): Promise<void> {
		const timeoutMs = options?.timeoutMs ?? 60_000;
		const deadline = Date.now() + timeoutMs;
		// A canvas remount (mode switch/HMR) disposes the engine mid-wait and swaps the ref.
		// Retry on the fresh ref instead of bubbling the disposed engine's rejection.
		while (Date.now() < deadline) {
			const instance = this.#threeInstanceRef as { waitForSceneReady?: (o?: { timeoutMs?: number }) => Promise<void> } | null;
			if (!instance) {
				await new Promise((resolve) => setTimeout(resolve, 100));
				continue;
			}
			try {
				return await instance.waitForSceneReady?.({ ...options, timeoutMs: Math.max(1000, deadline - Date.now()) });
			} catch (err) {
				const message = err instanceof Error ? err.message : String(err);
				if (!/engine disposed/i.test(message) || this.#threeInstanceRef === instance) throw err;
			}
		}
		throw new Error(`3D instance not mounted within ${timeoutMs}ms — cannot wait for scene ready`);
	}

	@Publish('rendering', 'updated')
	public setRendering (isRendering: boolean): void {
		this.#isRendering = isRendering;
	}
	public getRendering (): boolean { return this.#isRendering; }


	@Publish('object', 'selected')
	public selectObject (objectId: string): void {
		if (this.#threeInstanceRef) {
			this.#threeInstanceRef.highlightObject(objectId);
		}
	}

	/**
	 * Set or replace the blueprint. When called on an already-initialized component,
	 * the scene will transition according to the configured blueprintTransition mode.
	 */
	@Publish('blueprint', 'updated')
	public setBlueprint (blueprint: Blueprint): void {
		// Unsubscribe from OLD kitchen before reassigning
		this.#eventSubscriptionIds.forEach((sId) => this.#kitchen?.unsubscribe(sId));
		this.#eventSubscriptionIds = [];

		this.#pendingChanges = {};
		this.#microtaskScheduled = false;
		this.#blueprint = blueprint;
		this.#kitchen = blueprint.kitchens.getActiveVersion();
		this.#kitchenVersion = this.#kitchen?.version ?? null;
		this.#subscribeToBlueprintEvents();
	}

	/**
	 * @deprecated No longer used — scene updates are event-driven via microtask batching.
	 * Kept for interface compatibility. The callback is ignored.
	 */
	public setRebuild (_fn: () => void): void {
		// No-op: scene updates are now fully event-driven.
	}

	/**
	 * Slide all appliance meshes by a world-space delta without rebuilding
	 * @param delta positional delta in Three.js world units
	 * @param duration animation duration in ms (default 150)
	 */
	public slideAppliances (delta: { x: number; y: number; z: number }, duration?: number): void {
		this.#threeInstanceRef?.slideAppliances?.(delta, duration);
	}

	public render (): void {
		this.#forceRenderCount++;
	}

	get showTools (): boolean {
		return this.#showTools;
	}

	@Publish('show_tools', 'updated')
	set showTools (value: boolean) {
		this.#showTools = value;
	}

	@Publish('visibility', 'updated')
	set isVisible (visible: boolean) {
		this.#isVisible = visible;
	}
	get isVisible (): boolean {
		return this.#isVisible;
	}

	get progressiveRender (): boolean {
		return this.#progressiveRender;
	}

	@Publish('progressive_render', 'updated')
	set progressiveRender (value: boolean) {
		this.#progressiveRender = value;
	}

	get preloadAssets (): boolean {
		return this.#preloadAssets;
	}

	@Publish('preload_assets', 'updated')
	set preloadAssets (value: boolean) {
		this.#preloadAssets = value;
	}

	get noModelSkus (): string[] {
		return this.#noModelSkus;
	}

	set noModelSkus (value: string[]) {
		this.#noModelSkus = value;
	}

	public setThreeInstance (instance: UseThreeKitchenReturn): void {
		const incomingId = (instance as { instanceId?: string } | null)?.instanceId ?? null;
		const currentId = (this.#threeInstanceRef as { instanceId?: string } | null)?.instanceId ?? null;

		// A second live engine on one controller is the preview/capture mismatch bug: the slot holds
		// one engine, so whichever registered last silently answers every capture and export call.
		if (instance && currentId && incomingId !== currentId && this.#threeInstanceRef?.isActive?.()) {
			console.warn('[K3dCtrl] Replacing a live 3D instance — previews and captures may disagree', { incomingId, currentId });
		}

		this.#threeInstanceRef = instance;

		// A fresh engine knows nothing about the drag; hand it the domain side and the current switch
		instance?.setApplianceDragProvider?.(this.#dragProvider);
		instance?.setApplianceDragEnabled?.(this.#dragEnabled, this.#dragOptions);

		// Nor about hover. Enabling it before this instance attached only set the field, so without
		// this the pointer raycast is never wired and nothing glows — the state says on, the scene
		// never heard. Replaying it also survives a rebuild, which swaps the engine underneath.
		instance?.setHoverTrackingEnabled?.(this.#exploreHoverEnabled);

		// A rebuild replaces the island groups the badges hang off, so they are redrawn with them
		if (instance) this.#scheduleValidationMarkerRefresh();

		// Clear stale pending changes — setThreeInstance is called after a full
		// compose() or dissolveAndRebuild() that already produces a complete scene
		// from the current domain state. Flushing stale changes would duplicate
		// islands (the full build already added them, and rebuildIsland would add
		// them again). Any future domain events will accumulate fresh changes.
		if (instance && this.#hasPendingChanges()) {
			console.log('[K3dCtrl] Clearing stale pending changes after full build');
			this.#pendingChanges = {};
		}
	}

	/**
	 * Releases a 3D instance on unmount. Only clears the slot when `instance` is the registered
	 * one — an unconditional clear lets a closing preview blank the ref a still-mounted viewport
	 * owns, which strands every later capture, export and readiness wait.
	 * @param instance the adapter the caller registered
	 */
	public clearThreeInstance (instance: UseThreeKitchenReturn | null): void {
		const incomingId = (instance as { instanceId?: string } | null)?.instanceId ?? null;
		const currentId = (this.#threeInstanceRef as { instanceId?: string } | null)?.instanceId ?? null;
		if (incomingId && currentId && incomingId !== currentId) {
			console.log('[K3dCtrl] clearThreeInstance ignored — a different instance owns the slot', { incomingId, currentId });
			return;
		}
		this.#threeInstanceRef = null;
	}

	/**
	 * Rotates an appliance by updating its instanceRotation on the domain model.
	 * Called when the rotation gizmo drag completes.
	 * @param params objectId, axis, and angle (in radians)
	 */
	public rotateAppliance (params: { objectId: string; axis: 'x' | 'y' | 'z'; angle: number }): void {
		const { objectId, axis, angle } = params;
		console.log('[K3dCtrl] rotateAppliance called', { objectId, axis, angle, hasKitchen: !!this.#kitchen });
		if (!this.#kitchen) return;

		const refMap = this.#kitchen.getReferenceMap(objectId);
		console.log('[K3dCtrl] rotateAppliance refMap', { found: !!refMap, hasPanelItem: !!refMap?.panelItem, hasIsland: !!refMap?.island });
		if (!refMap) return;

		const { panelItem } = refMap;
		if (!panelItem) return;

		const current = panelItem.instanceRotation;
		// instanceRotation is stored in RADIANS (same as modelRotation on Component)
		const updated = new Vertex3({ dataIn: {
			x: current.x + (axis === 'x' ? angle : 0),
			y: current.y + (axis === 'y' ? angle : 0),
			z: current.z + (axis === 'z' ? angle : 0)
		} });
		panelItem.instanceRotation = updated;
		console.log('[K3dCtrl] rotateAppliance instanceRotation updated', { x: updated.x, y: updated.y, z: updated.z });

		// Queue a per-island rebuild so 3D cutouts update to match the rotated appliance
		const islandId = refMap.island?.id;
		if (islandId) {
			console.log('[K3dCtrl] rotateAppliance queueing islandResized rebuild for', islandId);
			this.#pendingChanges.islandResized = [...(this.#pendingChanges.islandResized || []), { islandId }];
			// Store the gizmo objectId so the microtask apply can re-activate
			// the gizmo on the new mesh after the rebuild completes.
			this.#pendingGizmoReactivation = objectId;
			this.#scheduleMicrotaskApply();
		}
	}

	// ── Appliance drag-and-drop ───────────────────────────────────────────────

	/**
	 * Switch press-and-hold drag-and-drop of appliances on or off. Options carry
	 * the app's rules (12 ft growth cap, island types that never grow, hold times).
	 */
	public setApplianceDragEnabled (enabled: boolean, options?: ApplianceDragOptions): void {
		this.#dragEnabled = enabled;
		if (options) this.#dragOptions = { ...this.#dragOptions, ...options };
		this.#threeInstanceRef?.setApplianceDragProvider?.(this.#dragProvider);
		this.#threeInstanceRef?.setApplianceDragEnabled?.(enabled, this.#dragOptions);
	}

	// ── Validation markers ────────────────────────────────────────────────────

	/**
	 * Show a severity badge under every appliance column the blueprint has a validation for.
	 * Clicking one publishes `blueprint_validation:show` so the app can surface that notification.
	 */
	public setValidationMarkersEnabled (enabled: boolean): void {
		this.#validationMarkersEnabled = enabled;
		if (enabled) this.refreshValidationMarkers();
		else this.#clearValidationMarkerRefresh();
		if (!enabled) {
			const islands = this.#threeInstanceRef?.getIslands?.() ?? [];
			hideValidationMarkersCfn(islands);
			this.#threeInstanceRef?.requestRender?.();
		}
	}

	get validationMarkersEnabled (): boolean { return this.#validationMarkersEnabled; }

	/** Redraw the badges from the kitchen's current notifications. */
	public refreshValidationMarkers (): void {
		this.#clearValidationMarkerRefresh();
		if (!this.#validationMarkersEnabled) return;

		const islands = this.#threeInstanceRef?.getIslands?.() ?? [];
		if (islands.length === 0) return;

		const markers = showValidationMarkersCfn({ kitchen: this.#kitchen, islands });
		this.#threeInstanceRef?.requestRender?.();
		this.publish('validation_markers', 'updated', { ref: this.#kitchen?.id ?? null, old: null, new: markers });
	}

	/**
	 * Redraw after the rules engine has had the same event: it subscribes to the kitchen too,
	 * so the badges must wait for the notifications it regenerates.
	 */
	#scheduleValidationMarkerRefresh (): void {
		if (!this.#validationMarkersEnabled || this.#validationMarkerRefresh) return;
		this.#validationMarkerRefresh = setTimeout(() => {
			this.#validationMarkerRefresh = null;
			this.refreshValidationMarkers();
		}, VALIDATION_MARKER_REFRESH_MS);
	}

	#clearValidationMarkerRefresh (): void {
		if (!this.#validationMarkerRefresh) return;
		clearTimeout(this.#validationMarkerRefresh);
		this.#validationMarkerRefresh = null;
	}

	// ── Explore hover ─────────────────────────────────────────────────────────

	/**
	 * Follow the pointer over the kitchen. Each move publishes `object:hover` with what is under it
	 * (`payload.id` null when nothing is), and the hovered part lights up. Off by default — hovering
	 * raycasts the scene, so only a consumer that wants it pays for it.
	 * @param enabled whether to follow the pointer
	 */
	public setExploreHoverEnabled (enabled: boolean): void {
		this.#exploreHoverEnabled = enabled;
		this.#threeInstanceRef?.setHoverTrackingEnabled?.(enabled);
	}

	get exploreHoverEnabled (): boolean { return this.#exploreHoverEnabled; }

	/**
	 * Light every part of the kitchen in one category — what a legend entry points at. Null clears.
	 * @param category `countertop`, `surround`, `appliance`, `safety`, or null
	 */
	public setCategoryHighlight (category: SceneCategory | null): void {
		this.#threeInstanceRef?.setCategoryHighlight?.(category);
	}

	/**
	 * Frame every part of one category — what a legend button zooms to. Safety frames the vents,
	 * which exist in the scene only as the proxies standing in for their holes.
	 * @param category `countertop`, `surround`, `appliance` or `safety`
	 * @returns false when the kitchen has nothing in that category to look at
	 */
	public async zoomToCategory (category: SceneCategory): Promise<boolean> {
		return (await this.#threeInstanceRef?.zoomToCategory?.(category)) ?? false;
	}

	// ── Explore markers ───────────────────────────────────────────────────────

	/**
	 * Show a `+` bubble on every surround, appliance and countertop. Clicking one goes out through
	 * the renderer's normal object-click bridge as `object:click` with `payload.type` of
	 * `explore-marker` and `payload.data` carrying `{ category, targetId, label }` — what the
	 * consuming app opens from there is its own business.
	 * @param enabled whether the bubbles are drawn
	 * @param categories which kinds to draw. Defaults to all three.
	 */
	public setExploreMarkersEnabled (enabled: boolean, categories?: Array<ExploreMarkerCategory>): void {
		this.#exploreMarkersEnabled = enabled;
		this.#exploreMarkerCategories = categories ?? EXPLORE_MARKER_CATEGORIES;
		if (enabled) {
			registerSceneObjectType({ type: EXPLORE_MARKER_TYPE, overlay: true });
			this.refreshExploreMarkers();
			return;
		}
		// The type stays registered: it is global, and another controller's bubbles may still be up.
		hideExploreMarkersCfn(this.#threeInstanceRef?.getIslands?.() ?? []);
		this.#threeInstanceRef?.requestRender?.();
	}

	get exploreMarkersEnabled (): boolean { return this.#exploreMarkersEnabled; }

	/** Redraw the bubbles against the scene as it stands now. */
	public refreshExploreMarkers (): void {
		if (!this.#exploreMarkersEnabled) return;

		const islands = this.#threeInstanceRef?.getIslands?.() ?? [];
		if (islands.length === 0) return;

		const markers = showExploreMarkersCfn({ islands, categories: this.#exploreMarkerCategories });
		this.#threeInstanceRef?.requestRender?.();
		this.publish('explore_markers', 'updated', { ref: this.#kitchen?.id ?? null, old: null, new: markers });
	}

	/**
	 * What the kitchen's validations would say after `request`: the same change the commit
	 * makes, applied to a throwaway copy of the blueprint (the rules engine runs on it as on
	 * the real one), compared with the notifications the kitchen carries now. Synchronous and
	 * about 100 ms on a two-island kitchen, so the drag manager asks only once the pointer pauses.
	 * Null when the copy or the change fails — the library refuses here the same way it would
	 * at the commit, and then there is nothing to report.
	 */
	public assessApplianceDrop (request: ApplianceDropRequest): ApplianceDropAssessment | null {
		const live = this.#kitchen;
		const blueprint = this.#blueprint;
		if (!live || !blueprint) return null;

		let copy: Kitchen | null = null;
		try {
			const clone = new Blueprint({ userEmail: 'SYSTEM', dataIn: blueprint.serialize() });
			copy = clone.kitchens.getActiveVersion() ?? null;
		} catch (error) {
			console.warn('[K3dCtrl] assessApplianceDrop: could not copy the blueprint', error);
			return null;
		}
		if (!copy) return null;

		const ref = copy.getReferenceMap(request.groupId);
		const group = ref?.panelItemGroup ?? null;
		const sourcePanel = ref?.panel ?? null;
		const sourceIsland = ref?.island ?? null;
		const targetIsland = copy.design.islands.children.find((i) => i.id === request.targetIslandId) ?? null;
		const targetPanel = targetIsland?.panels.getFrontPanel() ?? null;
		if (!group || !sourcePanel || !sourceIsland || !targetIsland || !targetPanel) return null;
		const growth = (island: Island) => ({ growable: this.#isGrowable(island), maxWidth: this.#dragOptions.maxIslandWidthIn ?? null });

		try {
			if (request.mode === 'fine-tune') {
				sourcePanel.fineTuneGroupPosition(group.id, request.x);
			} else if (request.mode === 'swap' && request.targetGroupId) {
				const targetRef = copy.getReferenceMap(request.targetGroupId);
				const targetGroup = targetRef?.panelItemGroup ?? null;
				if (!targetGroup) return null;
				if (targetRef?.panel?.id === sourcePanel.id) sourcePanel.swapApplianceGroupPositions(group.id, targetGroup.id);
				else swapApplianceGroupPositionsAcrossPanelsCfn(copy, group, targetGroup, { a: growth(sourceIsland), b: growth(targetIsland) });
			} else {
				moveApplianceGroupToPanelCfn(copy, group, targetPanel, request.x, growth(targetIsland));
			}
		} catch {
			return null;
		}

		const before = readKitchenIssuesCfn(live);
		const after = readKitchenIssuesCfn(copy);
		const beforeKeys = new Set(before.map((issue) => issue.key));
		const afterKeys = new Set(after.map((issue) => issue.key));
		return {
			issues: after.map(({ key, ...issue }) => ({ ...issue, isNew: !beforeKeys.has(key) })),
			resolvedCount: before.filter((issue) => !afterKeys.has(issue.key)).length
		};
	}

	/**
	 * Commit a drop: a positional move (same island or across islands) or a
	 * positional swap. The domain change happens with scene hints suspended and
	 * is followed by ONE instant-swap rebuild per touched island, which reads the
	 * live (previewed) mesh positions and slides everything the last few inches.
	 * Throws (and leaves the model untouched) when the library refuses.
	 */
	public async commitApplianceDrop (request: ApplianceDropRequest): Promise<ApplianceDropResult> {
		const kitchen = this.#kitchen;
		if (!kitchen) throw new Error('commitApplianceDrop: no kitchen');
		if (this.readonly) throw new Error('commitApplianceDrop: the design is read-only');

		const ref = kitchen.getReferenceMap(request.groupId);
		const group = ref?.panelItemGroup ?? null;
		const sourcePanel = ref?.panel ?? null;
		const sourceIsland = ref?.island ?? null;
		if (!group || !sourcePanel || !sourceIsland) throw new Error(`commitApplianceDrop: column '${request.groupId}' not found`);

		const targetIsland = kitchen.design.islands.children.find((i) => i.id === request.targetIslandId) ?? null;
		const targetPanel = targetIsland?.panels.getFrontPanel() ?? null;
		if (!targetIsland || !targetPanel) throw new Error(`commitApplianceDrop: island '${request.targetIslandId}' not found`);

		const sku = getGroupAnchorItemCfn(group.items.children)?.item.sku ?? '';
		const growth = (island: Island) => ({ growable: this.#isGrowable(island), maxWidth: this.#dragOptions.maxIslandWidthIn ?? null });

		let kind: ApplianceDropResult['kind'] = 'move';
		let swappedWithApplianceId: string | null = null;
		let movedIds: Array<string> = group.items.children.map((pi) => pi.id);
		let resized: ApplianceDropResult['resized'] = null;

		this.#hintsSuspended = true;
		try {
			if (request.mode === 'fine-tune') {
				// exactly where it was released, between the same neighbours; the distributor keeps it there
				sourcePanel.fineTuneGroupPosition(group.id, request.x);
				kind = 'fine-tune';
			} else if (request.mode === 'swap' && request.targetGroupId) {
				const targetRef = kitchen.getReferenceMap(request.targetGroupId);
				const targetGroup = targetRef?.panelItemGroup ?? null;
				if (!targetGroup) throw new Error(`commitApplianceDrop: swap target '${request.targetGroupId}' not found`);
				kind = 'swap';
				swappedWithApplianceId = getGroupAnchorItemCfn(targetGroup.items.children)?.id ?? null;
				if (targetRef?.panel?.id === sourcePanel.id) {
					sourcePanel.swapApplianceGroupPositions(group.id, targetGroup.id);
				} else {
					const result = swapApplianceGroupPositionsAcrossPanelsCfn(kitchen, group, targetGroup, { a: growth(sourceIsland), b: growth(targetIsland) });
					movedIds = result.movedIds;
					resized = result.resized.find((r) => r.islandId === targetIsland.id) ?? result.resized[0] ?? null;
				}
			} else {
				const result = moveApplianceGroupToPanelCfn(kitchen, group, targetPanel, request.x, growth(targetIsland));
				movedIds = result.movedIds;
				resized = result.resized;
			}
		} finally {
			this.#hintsSuspended = false;
		}

		// the reference vocabulary of move_appliance_group, read from the final order on the destination panel
		const destinationPanel = (kitchen.getReferenceMap(request.applianceId)?.panel as Panel | null) ?? targetPanel;
		const ordered = destinationPanel.panelItemGroups.children
			.filter((g) => g.items.children.some((pi) => !pi.item.category?.isVent && !pi.item.category?.isPassthrough))
			.sort((a, b) => a.center.x - b.center.x);
		const index = ordered.findIndex((g) => g.id === request.groupId);
		const neighbour = index > 0 ? ordered[index - 1] : (ordered.length > 1 ? ordered[index === 0 ? 1 : 0] : null);
		const position: ApplianceDropResult['position'] = index < 0 || !neighbour ? null : (index > 0 ? 'right' : 'left');
		const referenceApplianceId = neighbour ? getGroupAnchorItemCfn(neighbour.items.children)?.id ?? null : null;

		// one explicit rebuild per touched island
		const touched = Array.from(new Set([sourceIsland.id, targetIsland.id])).map((islandId) => ({ islandId }));
		this.#pendingChanges.islandResized = [...(this.#pendingChanges.islandResized ?? []), ...touched];
		await this.#flushPendingChangesNow();

		const result: ApplianceDropResult = {
			kind,
			applianceId: request.applianceId,
			sku,
			fromIslandId: sourceIsland.id,
			toIslandId: targetIsland.id,
			referenceApplianceId,
			position,
			swappedWithApplianceId,
			resized,
			movedIds
		};
		this.#publishObject('appliance', 'rearranged', result);
		return result;
	}

	#isGrowable (island: Island): boolean {
		const fixed = this.#dragOptions.fixedWidthIslandTypes ?? [];
		return !island.sizeLocked && !fixed.includes(String(island.type));
	}

	/** The drag's view of the kitchen: every island's front panel, columns, widths and growth rules. Null when there is nothing to drag. */
	#buildDragModel (): DragModel | null {
		const kitchen = this.#kitchen;
		if (!kitchen || this.readonly) return null;

		const islands: Array<DragIslandModel> = [];
		for (const island of kitchen.design.islands.children) {
			const panel = island.panels.getFrontPanel();
			if (!panel) continue;
			const input = panel.layoutInput;
			const groups = panel.panelItemGroups.children.map((g) => {
				const items = g.items.children;
				const anchor = getGroupAnchorItemCfn(items);
				const distributable = input.groups.find((ig) => ig.id === g.id)?.distributable ?? false;
				const hasAppliance = items.some((pi) => !isRuleGeneratedCompanionCfn(pi) && !pi.item.category?.isSpacer);
				return {
					groupId: g.id,
					itemIds: items.map((pi) => pi.id),
					anchorItemId: anchor?.id ?? items[0]?.id ?? '',
					sku: anchor?.item.sku ?? '',
					width: g.maxOutsideWidth,
					x: g.center.x,
					locked: g.positionLocked,
					fineTuned: g.fineTuned,
					distributable,
					draggable: distributable && !g.positionLocked && hasAppliance
				};
			});
			islands.push({
				islandId: island.id,
				type: String(island.type),
				panelId: panel.id,
				panelWidth: input.panelWidth,
				overlap: input.overlap,
				groups,
				widthIn: (island.size as { width: number }).width,
				heightIn: (island.size as { height: number }).height,
				growable: this.#isGrowable(island),
				minimumSizeWidth: island.minimumSize?.width ?? null
			});
		}
		return { islands };
	}

	#createDragProvider (): ApplianceDragProvider {
		return {
			getModel: () => this.#buildDragModel(),
			planLayout: (input: PlanLayoutInput) => planGroupLayoutCfn(input),
			predictGrowth: (island: DragIslandModel, groupWidths: Array<number>): GrowthPrediction => {
				const overlapTotal = island.overlap.left + island.overlap.right;
				const requiredWidth = getRequiredPanelWidthCfn(groupWidths, overlapTotal);
				const { fits, finalWidth } = computeAutoResizeWidthCfn({
					groupWidths,
					overlapTotal,
					currentWidth: island.widthIn,
					minimumWidth: requiredWidth,
					minimumSizeWidth: island.minimumSizeWidth,
					growOnly: true
				});
				return { fits, requiredWidth, finalWidth };
			},
			commitDrop: (request: ApplianceDropRequest) => this.commitApplianceDrop(request),
			assessDrop: (request: ApplianceDropRequest) => this.assessApplianceDrop(request),
			onState: (state: ApplianceDragState) => this.#publishObject('appliance', 'drag-state', state)
		};
	}

	#publishObject (noun: string, action: string, payload: unknown): void {
		(this.publish as unknown as (n: string, a: string, p: unknown, t: string) => void)(noun, action, payload, 'object');
	}

	/** Apply whatever hints are pending right now and resolve when the scene has caught up. */
	#flushPendingChangesNow (): Promise<void> {
		this.#applyAccumulatedChanges();
		return this.#applyChain;
	}

	/**
	 * Toggle visibility of a scene layer by object type without rebuilding the scene.
	 * @param type The userData.type to match (e.g. 'appliance', 'countertop', 'corner', 'debug')
	 * @param visible Whether the layer should be visible
	 */
	public setLayerVisibility (type: string, visible: boolean): void {
		this.#threeInstanceRef?.setLayerVisibility?.(type, visible);
	}

	/**
	 * Toggle debug cutout visualization (semi-transparent colored boxes).
	 * @param enabled Whether to show debug cutout shapes
	 */
	public setDebugCutouts (enabled: boolean): void {
		this.#threeInstanceRef?.setDebugCutouts?.(enabled);
	}

	/**
	 * Show or hide the brightness control in the 3D viewport.
	 * @param show Whether to show the brightness control
	 */
	public showControls (show: boolean): void {
		this.#threeInstanceRef?.showControls(show);
	}

	/**
	 * Set ambient brightness for the 3D scene.
	 * @param value Brightness level from 0 (dark) to 1 (full brightness)
	 */
	public setBrightness (value: number): void {
		(this.#threeInstanceRef as any)?.setBrightness?.(value);
	}

	/**
	 * Restore scene brightness to default preset values.
	 */
	public restoreBrightness (): void {
		(this.#threeInstanceRef as any)?.restoreBrightness?.();
	}

	@Publish('focal_offset', 'updated')
	set focalOffset (offset: { x: number; y: number }) {
		this.#focalOffset = {
			x: Number.isFinite(offset?.x) ? offset.x : 0,
			y: Number.isFinite(offset?.y) ? offset.y : 0
		};
	}
	get focalOffset (): { x: number; y: number } {
		return this.#focalOffset;
	}

	public setFocalOffset (offset: { x: number; y: number }): void {
		this.focalOffset = offset;
	}

	@Publish('viewport_anchor', 'updated')
	set viewportAnchor (anchor: { vertical?: number; horizontal?: number }) {
		this.#viewportAnchor = {
			vertical: Number.isFinite(anchor?.vertical) ? Math.max(0, Math.min(1, anchor.vertical)) : 0.5,
			horizontal: Number.isFinite(anchor?.horizontal) ? Math.max(0, Math.min(1, anchor.horizontal)) : 0.5
		};
	}
	get viewportAnchor (): { vertical?: number; horizontal?: number } {
		return this.#viewportAnchor;
	}

	public setViewportAnchor (anchor: { vertical?: number; horizontal?: number }): void {
		this.viewportAnchor = anchor;
	}

	static getToolDefinitions (): ToolDefinition[] {
		return getToolDefinitions();
	}

	public getDesignContext (): DesignContext {
		if (!this.#blueprint) {
			return { prompt: '## Current Design\n\n_No kitchen is loaded._', idMap: {} };
		}
		return this.#blueprint.getDesignContext();
	}

	/**
	 * Returns combined tool definitions from the controller and Blueprint (if available).
	 * Controller tools are for 3D visualization (camera, rendering, etc.).
	 * Blueprint tools are for design manipulation (materials, appliances, islands, etc.).
	 */
	public getToolDefinitions (): ToolDefinition[] {
		const controllerTools = Kitchen3dController.getToolDefinitions();

		// If Blueprint exists and supports tool definitions, merge them
		if (this.#blueprint && typeof this.#blueprint.getToolDefinitions === 'function') {
			try {
				const blueprintTools = this.#blueprint.getToolDefinitions().map((tool: ToolDefinition) => ({
					...tool,
					source: tool.source || 'blueprint'
				}));
				return [...controllerTools, ...blueprintTools];
			} catch {
				// Blueprint doesn't support tool definitions yet
			}
		}

		return controllerTools;
	}

	/**
	 * Subscribe to domain events to capture scene changes for targeted updates.
	 * Changes are accumulated in #pendingChanges and applied via microtask batching.
	 *
	 * Domain tool calls are synchronous — all @Publish events fire before the
	 * microtask flush, so a single microtask captures all events from one tool call.
	 *
	 * IMPORTANT: Subscriptions are placed on the Kitchen instance, NOT Blueprint.
	 * Events bubble via ControllerNodeBase.publishEvent() through instanceParent.
	 * Kitchen extends BlueprintBase → ControllerRootBase (no instanceParent),
	 * so events from PanelItem/Island/Panel bubble UP to Kitchen but stop there —
	 * they never reach Blueprint. Subscribing on Kitchen catches all domain events.
	 */
	#subscribeToBlueprintEvents (): void {
		// Unsubscribe old listeners before re-subscribing (prevents duplicates on setBlueprint)
		if (this.#eventSubscriptionIds.length > 0) {
			this.#eventSubscriptionIds.forEach((sId) => this.#kitchen?.unsubscribe(sId));
			this.#eventSubscriptionIds = [];
		}

		if (!this.#kitchen) return;

		// Helper to extract ID from event payload
		// EventRefType can be string | { id: string }, so handle both
		const getId = (e: any): string | undefined => {
			const ref = e?.payload?.ref;
			if (typeof ref === 'string') return ref;
			if (ref && typeof ref === 'object' && typeof ref.id === 'string') return ref.id;
			const id = e?.payload?.id || e?.ref || e?.id;
			if (typeof id === 'string') return id;
			if (id && typeof id === 'object' && typeof id.id === 'string') return id.id;
			return undefined;
		};

		const sub = (noun: string, action: string, cb: (e: any) => void): void => {
			const sId = this.#kitchen.subscribe(noun, action, (e: any) => {
				if (this.#hintsSuspended) return;
				cb(e);
			});
			if (sId) this.#eventSubscriptionIds.push(sId);
		};

		// Appliance moved — animate position
		sub('panel_item', 'moved', (e: any) => {
			const id = getId(e);
			const position = e?.payload?.position || e?.position;
			if (id && position) {
				this.#pendingChanges.moved = [...(this.#pendingChanges.moved || []), { id, position }];
				this.#scheduleMicrotaskApply();
			}
		});

		// Island removed — animate removal
		sub('island', 'deleted', (e: any) => {
			const id = getId(e);
			if (id) {
				this.#pendingChanges.islandsRemoved = [...(this.#pendingChanges.islandsRemoved || []), id];
				this.#scheduleMicrotaskApply();
			}
		});

		// Islands.delete() — animate removal
		sub('islands', 'deleted', (e: any) => {
			const id = getId(e);
			if (id) {
				this.#pendingChanges.islandsRemoved = [...(this.#pendingChanges.islandsRemoved || []), id];
				this.#scheduleMicrotaskApply();
			}
		});

		// Island added — build + fade-in new island
		sub('islands', 'added', (e: any) => {
			const id = getId(e);
			if (id) {
				this.#pendingChanges.islandsAdded = [...(this.#pendingChanges.islandsAdded || []), id];
				this.#scheduleMicrotaskApply();
			}
		});

		// Island positions updated — animate all island groups to new positions
		sub('islands_positions', 'updated', (_e: any) => {
			this.#pendingChanges.islandPositionsUpdated = true;
			this.#scheduleMicrotaskApply();
		});

		// Appliance added — per-island rebuild for the affected island.
		// The @Publish payload from newFromSku only contains the PanelItem id,
		// so resolve islandId from the domain model via getReferenceItem.
		sub('panel_items', 'add', (e: any) => {
			const id = getId(e);
			let islandId = e?.payload?.islandId || e?.islandId;
			const sku = e?.payload?.sku || e?.sku;
			if (id && !islandId && this.#kitchen) {
				const [, item] = this.#kitchen.getReferenceItem(id);
				islandId = (item as any)?.refMap?.island?.id;
			}
			if (id) {
				this.#pendingChanges.added = [...(this.#pendingChanges.added || []), { id, islandId, sku }];
				this.#scheduleMicrotaskApply();
			}
		});

		// Appliance removed — animate removal via fade-out.
		// KitchenDesignBase.cleanup() passes refMap as payload.ref, so extract
		// islandId from it for per-island rebuild after redistribution.
		sub('panel_item', 'deleted', (e: any) => {
			const id = getId(e);
			const ref = e?.payload?.ref;
			const islandId = (typeof ref === 'object') ? ref?.island?.id : undefined;
			if (id) {
				this.#pendingChanges.removed = [...(this.#pendingChanges.removed || []), id];
				if (islandId) {
					this.#pendingChanges.islandResized = [...(this.#pendingChanges.islandResized || []), { islandId }];
				}
				this.#scheduleMicrotaskApply();
			}
		});

		// Appliance swapped in place — per-island rebuild with cross-fade.
		// PanelItem.swapItem replaces the underlying Item without changing the
		// PanelItem id, so old/new ids are the same; islandId drives the rebuild.
		sub('panel_item_appliance', 'replaced', (e: any) => {
			const id = getId(e);
			if (!id) return;
			let islandId: string | undefined;
			if (this.#kitchen) {
				const [, item] = this.#kitchen.getReferenceItem(id);
				islandId = (item as any)?.refMap?.island?.id;
			}
			this.#pendingChanges.applianceReplaced = [
				...(this.#pendingChanges.applianceReplaced || []),
				{ oldId: id, newId: id, islandId }
			];
			this.#scheduleMicrotaskApply();
		});

		// Panel item group moved — diff + transition for redistributed appliances.
		// The @Publish setter payload uses ref = this.id (a string), so check
		// for string ref first before falling back to object property access.
		sub('panel_item_group', 'moved', (e: any) => {
			const ref = e?.payload?.ref;
			const groupId = (typeof ref === 'string') ? ref : ref?.id || ref?.panelItemGroup?.id || e?.ref?.id;
			if (groupId) {
				this.#pendingChanges.groupsMoved = [...(this.#pendingChanges.groupsMoved || []), { groupId }];
			}
			this.#scheduleMicrotaskApply();
		});

		// Island resized — per-island rebuild
		sub('island_size', 'updated', (e: any) => {
			const islandId = getId(e);
			console.log('[K3dCtrl] island_size.updated event received', { islandId, payload: e?.payload, hasThreeRef: Boolean(this.#threeInstanceRef) });
			if (islandId) {
				this.#pendingChanges.islandResized = [...(this.#pendingChanges.islandResized || []), { islandId }];
			}
			this.#scheduleMicrotaskApply();
		});

		// Island rotated — animate rotation via registry['island'].rotate
		sub('island_position', 'updated', (e: any) => {
			const islandId = getId(e);
			if (islandId) {
				this.#pendingChanges.islandRotated = [...(this.#pendingChanges.islandRotated || []), { islandId }];
			}
			this.#scheduleMicrotaskApply();
		});

		// Panel deleted — per-island rebuild for containing island
		sub('panel', 'deleted', (e: any) => {
			const islandId = e?.payload?.islandId || e?.islandId || getId(e);
			if (islandId) {
				this.#pendingChanges.panelDeleted = [...(this.#pendingChanges.panelDeleted || []), { islandId }];
			}
			this.#scheduleMicrotaskApply();
		});

		// Surround material changed — texture swap or per-island rebuild for slat transitions.
		// Island's own subscriber fires first (earlier registration) and triggers cascading
		// panel resize / vent removal+addition events that accumulate as removed/added/
		// islandPositionsUpdated hints. Clean those up so the rebuild handles everything.
		sub('surround_material', 'updated', (e: any) => {
			const key = e?.payload?.new?.[0];
			if (key) {
				this.#pendingChanges.surroundMaterialChanged = { key };
				delete this.#pendingChanges.removed;
				delete this.#pendingChanges.added;
				delete this.#pendingChanges.islandPositionsUpdated;
				this.#scheduleMicrotaskApply();
			}
		});

		// Countertop material changed — texture swap
		sub('countertop_material', 'updated', (e: any) => {
			const key = e?.payload?.new?.[0];
			if (key) {
				this.#pendingChanges.countertopMaterialChanged = { key };
				this.#scheduleMicrotaskApply();
			}
		});

		// Component model rotation changed — per-island rebuild for affected islands.
		// Component events bubble through Item.publishEvent, so the Kitchen sees them.
		// The payload ref is the Component ID (not a KitchenDesignBase entity), so we
		// rebuild all islands since resolving the specific island is not straightforward.
		sub('component_model_rotation', 'updated', (_e: any) => {
			const islands = this.#kitchen?.design?.islands?.children ?? [];
			for (const island of islands) {
				this.#pendingChanges.islandResized = [...(this.#pendingChanges.islandResized || []), { islandId: island.id }];
			}
			if (islands.length > 0) this.#scheduleMicrotaskApply();
		});

		// Placeholder model SKU changed — per-island rebuild so the affected appliance's
		// model file is re-resolved (placeholderModelSku || sku) and re-loaded with the
		// salmon-red tint + user-entered size scaling applied. Same rationale as
		// component_model_rotation: the @Publish payload ref is the Component ID, not a
		// KitchenDesignBase entity, so we rebuild every island to cover all instances of
		// that catalog product (placeholderModelSku is admin-set globally per Component).
		sub('component', 'placeholder_model_sku_updated', (_e: any) => {
			const islands = this.#kitchen?.design?.islands?.children ?? [];
			for (const island of islands) {
				this.#pendingChanges.islandResized = [...(this.#pendingChanges.islandResized || []), { islandId: island.id }];
			}
			if (islands.length > 0) this.#scheduleMicrotaskApply();
		});

		// Validations follow any mutation. Registered outside `sub` so a drag commit, which
		// suspends scene hints, still redraws its badges once the rules engine has run.
		const markerSub = this.#kitchen.subscribe('*', ['added', 'deleted', 'updated', 'moved'], () => this.#scheduleValidationMarkerRefresh());
		if (markerSub) this.#eventSubscriptionIds.push(markerSub);
	}

	/**
	 * Check if there are any pending scene changes from domain events.
	 */
	#hasPendingChanges (): boolean {
		const pc = this.#pendingChanges;
		return Boolean(
			pc.removed?.length ||
			pc.islandsRemoved?.length ||
			pc.islandsAdded?.length ||
			pc.islandsMoved?.length ||
			pc.added?.length ||
			pc.moved?.length ||
			pc.materials?.length ||
			pc.groupsMoved?.length ||
			pc.islandResized?.length ||
			pc.islandRotated?.length ||
			pc.islandPositionsUpdated ||
			pc.panelDeleted?.length ||
			pc.applianceReplaced?.length ||
			pc.surroundMaterialChanged ||
			pc.countertopMaterialChanged
		);
	}

	/**
	 * Schedule a microtask to apply accumulated changes.
	 * Domain tool calls are synchronous — all @Publish events fire before the
	 * microtask flush runs, so one microtask captures all events from a single
	 * tool call. This replaces both the old #inferPendingChanges() approach and
	 * the 50ms setTimeout debounce.
	 */
	#scheduleMicrotaskApply (): void {
		if (this.#microtaskScheduled) return;
		this.#microtaskScheduled = true;
		queueMicrotask(() => {
			this.#microtaskScheduled = false;
			this.#applyAccumulatedChanges();
		});
	}

	/**
	 * Apply all accumulated pending changes via the engine's targeted update system.
	 * Drains pending changes and appends a serial chain of applyTargetedChanges so
	 * same-island rebuilds stay ordered, but the microtask itself does NOT await —
	 * consecutive tool calls can queue their applies immediately, letting slide/fade
	 * animations from a prior apply overlap with rebuilds of the next.
	 */
	#applyAccumulatedChanges (): void {
		if (!this.#hasPendingChanges()) return;

		if (!this.#threeInstanceRef?.applyTargetedChanges) {
			console.warn('[K3dCtrl] No threeInstanceRef — pending changes deferred until setThreeInstance');
			// Keep #pendingChanges intact — setThreeInstance will flush them.
			// Do NOT schedule here: without a ref the changes can never be
			// consumed, so rescheduling creates an infinite microtask loop that
			// starves the browser event loop and freezes the UI.
			return;
		}

		this.#logPendingChanges('Microtask apply firing');
		const changes = this.#pendingChanges;
		this.#pendingChanges = {};
		const gizmoObjectId = this.#pendingGizmoReactivation;
		this.#pendingGizmoReactivation = null;
		this.#applyChain = this.#applyChain
			.then(() => this.#threeInstanceRef!.applyTargetedChanges!(changes))
			.then(() => {
				// Re-activate the rotation gizmo on the new mesh after the rebuild
				// replaces the island group (old mesh destroyed, new mesh created).
				if (gizmoObjectId && this.#threeInstanceRef?.deactivateRotationGizmo) {
					console.log('[K3dCtrl] Re-activating gizmo on new mesh', gizmoObjectId);
					this.#threeInstanceRef.deactivateRotationGizmo();
					this.#threeInstanceRef.activateRotationGizmo(gizmoObjectId);
					console.log('[K3dCtrl] Gizmo re-activated');
				} else {
					console.log('[K3dCtrl] Gizmo reactivation skipped', { gizmoObjectId, hasDeactivate: !!this.#threeInstanceRef?.deactivateRotationGizmo });
				}
			})
			.catch((err) => {
				console.error('[K3dCtrl] applyTargetedChanges failed', err);
			});
	}

	/**
	 * Executes a tool by name. The controller acts as a proxy:
	 * - 3D controller tools (camera, rendering) are executed locally
	 * - Blueprint tools (materials, appliances) are proxied to Blueprint
	 * - If no Blueprint exists, Blueprint tool calls are a noop (silent success)
	 *
	 * Scene updates are fully event-driven: tool execution fires @Publish events
	 * synchronously, which accumulate in #pendingChanges. The microtask flush
	 * applies all changes via the TransitionConfigRegistry after the tool returns.
	 */
	public async executeTool (name: string, args: any): Promise<ToolResult> {
		const context: ToolContext = {
			threeInstance: this.#threeInstanceRef,
			options: this.options,
			publish: (noun: string, action: string, payload: any) => {
				this.publish(noun, action, payload);
			},
			setImage: (imageData: string) => this.setImage(imageData),
			selectObject: (objectId: string) => this.selectObject(objectId),
			controller: this,
			kitchen: this.#kitchen
		};

		const result = await executeTool(name, args, context, this.#blueprint);
		// Events fired synchronously during tool execution.
		// Microtask flush applies accumulated changes automatically.
		return result;
	}
}
